import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import type { CourseModel } from '@/models/CourseModels';
import { resolveType } from '@/services/ServiceContainer';
import { CourseService } from '@/services/CourseService';

const CourseScorecard: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (location.state?.courseId) {
            const loadCourse = async () => {
                try {
                    const courseService = resolveType(CourseService);
                    const courseModel = await courseService.get(location.state.courseId);
                    if (courseModel) {
                        setCourseModel(courseModel);
                    }
                } catch (error) {
                    console.error('Error loading course:', error);
                } finally {
                    setLoading(false);
                }
            };

            loadCourse();
        }
    }, [location.state]);

    if (!courseModel || loading) {
        return (
            <div style={{ padding: theme.spacing.MEDIUM, textAlign: 'center' }}>
                Loading...
            </div>
        );
    }

    return (
        <div style={{ padding: theme.spacing.MEDIUM }}>
            <h1 style={{
                color: theme.colors.TEXT,
                marginBottom: theme.spacing.LARGE,
                textAlign: 'center'
            }}>
                {courseModel.name}
            </h1>

            <div style={{
                backgroundColor: theme.colors.BACKGROUND,
                padding: theme.spacing.MEDIUM,
                borderRadius: theme.borderRadius.MEDIUM,
                marginBottom: theme.spacing.LARGE
            }}>
                <h2 style={{ color: theme.colors.TEXT, marginBottom: theme.spacing.MEDIUM }}>
                    Course Information
                </h2>
                <p style={{ color: theme.colors.TEXT }}>
                    {courseModel.location.address.full}
                </p>
                <p style={{ color: theme.colors.TEXT }}>
                    Phone: {courseModel.location.phoneNumber}
                </p>
            </div>

            <div style={{
                overflowX: 'auto',
                marginBottom: theme.spacing.LARGE
            }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: theme.colors.BACKGROUND,
                    borderRadius: theme.borderRadius.MEDIUM,
                    overflow: 'hidden'
                }}>
                    <thead>
                        <tr style={{ backgroundColor: theme.colors.PRIMARY, color: theme.colors.BACKGROUND }}>
                            <th style={{ padding: theme.spacing.SMALL }}>Hole</th>
                            <th style={{ padding: theme.spacing.SMALL }}>Par</th>
                            <th style={{ padding: theme.spacing.SMALL }}>Handicap</th>
                            {courseModel.tees.map(tee => (
                                <th key={tee.teeName} style={{ padding: theme.spacing.SMALL }}>
                                    {tee.teeName}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {courseModel.scorecard.holes.map((hole, index) => (
                            <tr key={index} style={{
                                borderBottom: `1px solid ${theme.colors.BORDER}`,
                                color: theme.colors.TEXT
                            }}>
                                <td style={{ padding: theme.spacing.SMALL, textAlign: 'center' }}>{hole.number}</td>
                                <td style={{ padding: theme.spacing.SMALL, textAlign: 'center' }}>{hole.par}</td>
                                <td style={{ padding: theme.spacing.SMALL, textAlign: 'center' }}>{hole.mensHandicap18}</td>
                                {courseModel.tees.map(tee => {
                                    const teeInfo = hole.tees.find(t => t.name === tee.teeName);
                                    return (
                                        <td key={tee.teeName} style={{ padding: theme.spacing.SMALL, textAlign: 'center' }}>
                                            {teeInfo?.yards || '-'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <button
                onClick={() => navigate('/dashboard')}
                style={{
                    width: '100%',
                    padding: theme.spacing.MEDIUM,
                    backgroundColor: theme.colors.PRIMARY,
                    color: theme.colors.BACKGROUND,
                    border: 'none',
                    borderRadius: theme.borderRadius.MEDIUM,
                    cursor: 'pointer',
                    fontSize: '1.2em'
                }}
            >
                Return to Dashboard
            </button>
        </div>
    );
};

export default CourseScorecard; 